import { AfterViewInit, Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import { Router } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Handsontable from 'handsontable';
import { EntityKey } from '../../entity/entity';
import { EntityService } from '../../services/entity-service';
import { RiserChartUnit } from '../../riser/riser-chart-unit';
import { RiserSystemType } from '../../riser/riser-system-type';
import { MessageLevel, RiserUploadResponse } from '../../riser/riser-upload-response';
import { RiserUploadService } from '../../services/riser-upload-service';
import { FileUtils } from '../../utils/file-utils';
import { UploadFile } from '../../documents/upload-file';
import { StringUtils } from '../../utils/string-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ConfigurationMessagesDialogComponent } from '../../configurator/common/configuration-messages-dialog/configuration-messages-dialog.component';

function naturalCompare(a: string, b: string): number {
  const ax: any = [];
  const bx: any = [];
  (a || '').replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
    ax.push([$1 || Infinity, $2 || '']);
    return '';
  });
  (b || '').replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
    bx.push([$1 || Infinity, $2 || '']);
    return '';
  });
  while (ax.length && bx.length) {
    const an = ax.shift();
    const bn = bx.shift();
    const nn = an[0] - bn[0] || an[1].localeCompare(bn[1]);
    if (nn) return nn;
  }
  return ax.length - bx.length;
}

const VALID_DIAMETERS = [0.75, 1.0, 1.25, 1.5, 2.0, 2.5, 3.0];
const DRAIN_VALID_DIAMETERS = [1.0, 1.25];
const TUBE_OUTSIDE_DIAMETERS: Record<number, number> = { 0.75: 0.875, 1: 1.125, 1.25: 1.375, 1.5: 1.625, 2: 2.125, 2.5: 2.625, 3: 3.125 };
const CABINET_TIER_WIDTHS = [17, 20, 24];
const CABINET_SIZE_TIERS: Record<string, number> = { '03': 0, '04': 0, '06': 1, '08': 1, '10': 2, '12': 2 };
const MEGA_MOD_SIZES = ['14', '16', '18', '20'];
const MEGA_MOD_WIDTH = 30;
const CABINET_FOOTPRINT_STEPS: Record<string, number> = { STD: 0, '1UP': 1, '2UP': 2 };
const STANDARD_GAP_INNER = 3;
const STANDARD_GAP_OUTER = 3.625;
const UPSIZE_NOTES = ['', 'SFR Note-Needs Single Upsize or SFR', 'SFR Note-Needs Double Upsize or SFR'];
const SPACING_TOO_SMALL_NOTE = 'SFR Note-Cabinet spacing too small';
const DIAMETER_OPTIONS = [
  { id: -1, description: 'None', },
  { id: 0, description: 'Capped' },
  { id: 0.75, description: '3/4"' },
  { id: 1, description: '1"' },
  { id: 1.25, description: '1 1/4"' },
  { id: 1.5, description: '1 1/2"' },
  { id: 2, description: '2"' },
  { id: 2.5, description: '2 1/2"' },
  { id: 3, description: '3"' }
];
const DIAMETER_DESCRIPTIONS = DIAMETER_OPTIONS.map((o) => o.description);
function diameterValidator(value: string, callback: any) {
  if (value === null || value === undefined || value === '') {
    callback(true);
    return;
  }
  const num = +value;
  const desc = String(value);
  callback(DIAMETER_OPTIONS.some((o) => o.id === num || o.description === desc));
}
const INSULATION_OPTIONS = [
  { id: '0', description: 'None' },
  { id: '50', description: '1/2"' },
  { id: '75', description: '3/4"' },
  { id: '100', description: '1"' },
  { id: '150', description: '1 1/2"' },
];
const INSULATION_DESCRIPTIONS = INSULATION_OPTIONS.map((o) => o.description);
const DRAIN_INSULATION_OPTIONS = [
  { id: '0', description: 'None' },
  { id: '50', description: '1/2"' },
];
const DRAIN_INSULATION_DESCRIPTIONS = DRAIN_INSULATION_OPTIONS.map((o) => o.description);
const EXTENSION_LOCATION_OPTIONS = ['', 'Top', 'Bottom'];

function diameterDescription(value: any): string {
  if (value === null || value === undefined || value === '') return '';
  const parsed = +value;
  const match = DIAMETER_OPTIONS.find((o) => o.id === parsed);
  return match ? match.description : value;
}

function diameterId(value: any): any {
  if (value === null || value === undefined || value === '') return value;
  const str = String(value);
  const match = DIAMETER_OPTIONS.find((o) => o.description === str);
  return match ? match.id : value;
}

function diameterRenderer(this: unknown, instance: any, td: HTMLElement, row: number, col: number, prop: string | number, value: any, cellProperties: any): HTMLElement {
  const display = diameterDescription(value);
  Handsontable.renderers.AutocompleteRenderer.apply(this, [instance, td, row, col, prop, display, cellProperties]);
  return td;
}

function insulationDescription(value: any): string {
  if (value === null || value === undefined || value === '') return '';
  const str = String(value);
  const match = INSULATION_OPTIONS.find((o) => o.id === str || o.description === str);
  return match ? match.description : str;
}

function insulationId(value: any): any {
  if (value === null || value === undefined || value === '') return value;
  const str = String(value);
  const match = INSULATION_OPTIONS.find((o) => o.description === str);
  return match ? match.id : value;
}

function insulationRenderer(this: unknown, instance: any, td: HTMLElement, row: number, col: number, prop: string | number, value: any, cellProperties: any): HTMLElement {
  const display = insulationDescription(value);
  Handsontable.renderers.AutocompleteRenderer.apply(this, [instance, td, row, col, prop, display, cellProperties]);
  return td;
}

function insulationValidator(value: any, callback: any) {
  if (value === null || value === undefined || value === '') {
    callback(true);
    return;
  }
  const str = String(value);
  callback(INSULATION_OPTIONS.some((o) => o.id === str || o.description === str));
}

function drainInsulationDescription(value: any): string {
  if (value === null || value === undefined || value === '') return '';
  const str = String(value);
  const match = DRAIN_INSULATION_OPTIONS.find((o) => o.id === str || o.description === str);
  return match ? match.description : str;
}

function drainInsulationId(value: any): any {
  if (value === null || value === undefined || value === '') return value;
  const str = String(value);
  const match = DRAIN_INSULATION_OPTIONS.find((o) => o.description === str);
  return match ? match.id : value;
}

function drainInsulationRenderer(this: unknown, instance: any, td: HTMLElement, row: number, col: number, prop: string | number, value: any, cellProperties: any): HTMLElement {
  const display = drainInsulationDescription(value);
  Handsontable.renderers.AutocompleteRenderer.apply(this, [instance, td, row, col, prop, display, cellProperties]);
  return td;
}

function drainInsulationValidator(value: any, callback: any) {
  if (value === null || value === undefined || value === '') {
    callback(true);
    return;
  }
  const str = String(value);
  callback(DRAIN_INSULATION_OPTIONS.some((o) => o.id === str || o.description === str));
}

@Component({
  selector: 'app-riser-chart',
  templateUrl: './riser-chart.component.html',
  styleUrls: ['./riser-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner, NgClass, FormsModule],
})
export class RiserChartComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly entityName = input.required<string>({ alias: 'entity-name' });
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private readonly riserUploadService = inject(RiserUploadService);
  private readonly entityService = inject(EntityService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  readonly entityKey = signal<EntityKey>(undefined!);
  readonly riserData = signal<RiserChartUnit[]>([]);
  handsontable!: Handsontable;
  readonly isBusy = signal<boolean>(false);
  readonly messages = signal<string[]>([]);
  readonly uploadResponse = signal<RiserUploadResponse>(undefined!);
  readonly hasValidationErrors = signal<boolean>(false);
  readonly readOnly = signal<boolean>(false);
  filterBuilding = '';
  filterRiser = '';
  filterFloor = '';
  filterRiserGroup = '';
  readonly buildingOptions = signal<string[]>([]);
  readonly riserOptions = signal<string[]>([]);
  readonly floorOptions = signal<string[]>([]);
  readonly riserGroupOptions = signal<string[]>([]);

  ngOnInit(): void {
    this.entityKey.set(new EntityKey());
    this.entityKey().entityName = this.entityName();
    this.entityKey().entityId = this.entityId();
    this.entityKey().entityRevision = +this.entityRevision();
  }

  ngAfterViewInit(): void {
    if (this.entityKey().entityName === 'ORDER' || this.entityKey().entityName === 'CONTRACT') {
      this.isBusy.set(true);
      this.entityService.getEntityByKey(this.entityKey()).subscribe({
        next: (entity) => {
          this.readOnly.set(entity.approvalStatus != null && entity.approvalStatus.indexOf('Approved') >= 0);
          this.isBusy.set(false);
          this.loadData();
        },
        error: (error) => {
          this.isBusy.set(false);
          this.messages.update(current => [...current, 'Error loading entity: ' + StringUtils.getErrorMessage(error)]);
        },
      });
    } else {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    if (this.handsontable) {
      this.handsontable.destroy();
    }
  }

  goBack(): void {
    if (this.entityKey() && this.entityKey().entityName) {
      const pageName = this.entityKey().entityName === 'QUOTE' ? 'quotes' : 'orders';
      this.router.navigate([pageName, this.entityKey().entityId, this.entityKey().entityRevision]);
    } else {
      this.router.navigate(['main']);
    }
  }

  private loadData(): void {
    this.isBusy.set(true);
    this.messages.set([]);
    this.riserUploadService.getRiserData(this.entityKey()).subscribe({
      next: (data) => {
        this.riserData.set(data);
        this.buildFilterOptions();
        this.initTable();
        this.isBusy.set(false);
      },
      error: (error) => {
        this.isBusy.set(false);
        this.messages.update(current => [...current, 'Error loading riser data: ' + StringUtils.getErrorMessage(error)]);
      },
    });
  }

  private buildFilterOptions(): void {
    const buildings = new Set<string>();
    const risers = new Set<string>();
    const floors = new Set<string>();
    const riserGroups = new Set<string>();
    for (const row of this.riserData()) {
      if (row.building) buildings.add(row.building);
      if (row.riser) risers.add(row.riser);
      if (row.floor) floors.add(row.floor);
      if (row.building || row.riser) {
        riserGroups.add((row.building || '') + ' / ' + (row.riser || ''));
      }
    }
    this.buildingOptions.set(Array.from(buildings).sort(naturalCompare));
    this.riserOptions.set(Array.from(risers).sort(naturalCompare));
    this.floorOptions.set(Array.from(floors).sort(naturalCompare));
    this.riserGroupOptions.set(Array.from(riserGroups).sort(naturalCompare));
  }

  private getFilteredData(): any[] {
    return this.riserData()
      .filter((r) => !this.filterBuilding || r.building === this.filterBuilding)
      .filter((r) => !this.filterRiser || r.riser === this.filterRiser)
      .filter((r) => !this.filterFloor || r.floor === this.filterFloor)
      .filter((r) => {
        if (!this.filterRiserGroup) return true;
        const group = (r.building || '') + ' / ' + (r.riser || '');
        return group === this.filterRiserGroup;
      })
      .sort((a, b) => naturalCompare(a.building || '', b.building || '') || naturalCompare(a.riser || '', b.riser || '') || naturalCompare(a.floor || '', b.floor || ''))
      .map((r) => ({ ...r, status: '' }));
  }

  onFilterChange(): void {
    if (!this.handsontable) return;
    this.syncEditsToSource();
    const filtered = this.getFilteredData();
    this.handsontable.loadData(filtered);
    this.runValidation();
  }

  private syncEditsToSource(): void {
    if (!this.handsontable) return;
    const tableData = this.handsontable.getSourceData() as any[];
    for (const row of tableData) {
      const match = this.riserData().find((r) => r.lineId === row.lineId && r.sequence === row.sequence);
      if (match) {
        Object.assign(match, row);
        delete (match as any).status;
      }
    }
  }

  clearFilters(): void {
    this.filterBuilding = '';
    this.filterRiser = '';
    this.filterFloor = '';
    this.filterRiserGroup = '';
    this.onFilterChange();
  }

  private initTable(): void {
    const container = document.getElementById('riserChartTable');
    if (!container) return;

    const tableData = this.getFilteredData();

    const columns = [
      { data: 'lineId', readOnly: true, className: 'read-only-column' },
      { data: 'tag', readOnly: true, className: 'read-only-column' },
      { data: 'riser', readOnly: true, className: 'read-only-column' },
      { data: 'floor', readOnly: true, className: 'read-only-column' },
      { data: 'room', readOnly: true, className: 'read-only-column' },
      { data: 'building', readOnly: true, className: 'read-only-column' },
      { data: 'poLine', readOnly: true, className: 'read-only-column' },
      { data: 'status', readOnly: true, renderer: (instance: any, td: any, row: any, col: any, prop: any, value: any) => this.statusRenderer(td, value) },
      { data: 'modelSize', readOnly: true, className: 'read-only-column' },
      { data: 'coil', readOnly: true, className: 'read-only-column' },
      { data: 'arrangement', readOnly: true, className: 'read-only-column' },
      { data: 'coldSupplyTop', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'coldSupplyBottom', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'coldSupplyInsulation', type: 'dropdown', source: INSULATION_DESCRIPTIONS, renderer: insulationRenderer, validator: insulationValidator },
      { data: 'coldReturnTop', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'coldReturnBottom', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'coldReturnInsulation', type: 'dropdown', source: INSULATION_DESCRIPTIONS, renderer: insulationRenderer, validator: insulationValidator },
      { data: 'drainTop', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'drainBottom', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'drainInsulation', type: 'dropdown', source: DRAIN_INSULATION_DESCRIPTIONS, renderer: drainInsulationRenderer, validator: drainInsulationValidator },
      { data: 'hotSupplyTop', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'hotSupplyBottom', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'hotSupplyInsulation', type: 'dropdown', source: INSULATION_DESCRIPTIONS, renderer: insulationRenderer, validator: insulationValidator },
      { data: 'hotReturnTop', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'hotReturnBottom', type: 'dropdown', source: DIAMETER_DESCRIPTIONS, validator: diameterValidator, allowInvalid: true, renderer: diameterRenderer },
      { data: 'hotReturnInsulation', type: 'dropdown', source: INSULATION_DESCRIPTIONS, renderer: insulationRenderer, validator: insulationValidator },
      { data: 'length' },
      { data: 'extensionLocation', type: 'dropdown', source: EXTENSION_LOCATION_OPTIONS },
      { data: 'extensionLength' },
      { data: 'sequence', readOnly: true, className: 'read-only-column' },
      { data: 'heatKw', readOnly: true, className: 'read-only-column' },
      { data: 'voltage', readOnly: true, className: 'read-only-column' },
      { data: 'primaryCtrlValve', readOnly: true, className: 'read-only-column' },
      { data: 'primaryGpm', readOnly: true, className: 'read-only-column' },
      { data: 'secondaryCtrlValve', readOnly: true, className: 'read-only-column' },
      { data: 'secondaryGpm', readOnly: true, className: 'read-only-column' },
    ];

    if (this.readOnly()) {
      columns.forEach((col) => {
        (col as any).readOnly = true;
        (col as any).className = 'read-only-column';
      });
    }

    const colHeaders = [
      'Line',
      'Tag',
      'Riser',
      'Floor',
      'Room',
      'Bldg',
      'POL',
      'Status',
      'Model/Size',
      'Coil',
      'Arrangement',
      'CldSup Top',
      'CldSup Bot',
      'CldSup Insul',
      'CldRet Top',
      'CldRet Bot',
      'CldRet Insul',
      'Drain Top',
      'Drain Bot',
      'Drain Insul',
      'HotSup Top',
      'HotSup Bot',
      'HotSup Insul',
      'HotRet Top',
      'HotRet Bot',
      'HotRet Insul',
      'Length',
      'Ext Location',
      'Ext Length',
      'Seq',
      'Heat Kw',
      'Voltage',
      'Pri Ctrl Valve',
      'Pri GPM',
      'Sec Ctrl Valve',
      'Sec GPM',
    ];

    const options = {
      data: tableData,
      columns: columns,
      colHeaders: colHeaders,
      rowHeaders: true,
      fixedColumnsLeft: 8,
      stretchH: 'all',
      height: this.calculateTableHeight(container),
      allowInsertRow: false,
      allowRemoveRow: false,
      allowInsertColumn: false,
      allowRemoveColumn: false,
      fillHandle: false,
      manualColumnResize: true,
      columnSorting: true,
      sortFunction: (sortOrder: any, columnMeta: any) => {
        return (a: any, b: any) => {
          const valA = a[1] == null ? '' : String(a[1]);
          const valB = b[1] == null ? '' : String(b[1]);
          const result = naturalCompare(valA, valB);
          return sortOrder === 'desc' ? -result : result;
        };
      },
    };
    this.handsontable = new Handsontable(container, options as any);
    this.handsontable.addHook('beforeChange', [
      (changes: any) => {
        if (!changes) return;
        for (const change of changes) {
          if (change && change[1]) {
            if (change[1].indexOf('Insulation') >= 0) {
              if (change[3] != null) {
                if (change[1] === 'drainInsulation') {
                  change[3] = drainInsulationId(change[3]);
                } else {
                  change[3] = insulationId(change[3]);
                }
              }
            } else if (change[1].endsWith('Top') || change[1].endsWith('Bottom')) {
              if (change[3] != null) {
                  change[3] = diameterId(change[3]);
              }
            }
          }
        }
      },
    ]);
    this.handsontable.addHook('afterChange', [
      (changes: any, source: any) => {
        if (source !== 'loadData' && source !== 'validation') {
          this.runValidation();
        }
      },
    ]);
    this.runValidation();
  }

  private statusRenderer(td: HTMLElement, value: string): HTMLElement {
    td.style.maxWidth = '200px';
    td.style.overflow = 'hidden';
    td.style.textOverflow = 'ellipsis';
    td.style.whiteSpace = 'nowrap';
    td.title = value || '';
    td.innerHTML = '';
    td.style.cursor = '';

    // Remove previously attached click handler if any
    if ((td as any)._statusClickHandler) {
      td.removeEventListener('click', (td as any)._statusClickHandler);
      (td as any)._statusClickHandler = null;
    }

    if (!value || value === 'OK') {
      td.innerText = value || '';
    } else {
      const messages = value.replace(/^Note: /, '').split('; ');
      if (messages.length <= 2) {
        td.innerText = value;
      } else {
        td.innerText = '\u26A0 ' + messages.length + ' issues (click to view)';
        td.style.cursor = 'pointer';
        const handler = () => this.showStatusMessages(messages);
        (td as any)._statusClickHandler = handler;
        td.addEventListener('click', handler);
      }
    }

    if (value === 'OK') {
      td.style.backgroundColor = '#d4edda';
      td.style.color = '#155724';
    } else if (value && value.startsWith('Note:')) {
      td.style.backgroundColor = '#fff3cd';
      td.style.color = '#856404';
    } else if (value && value.length > 0) {
      td.style.backgroundColor = '#f8d7da';
      td.style.color = '#721c24';
    } else {
      td.style.backgroundColor = '';
      td.style.color = '';
    }
    return td;
  }

  private showStatusMessages(messages: string[]): void {
    this.dialog.open(ConfigurationMessagesDialogComponent, {
      data: { messages: messages },
      width: '500px',
    });
  }

  private runValidation(): void {
    if (!this.handsontable) return;
    const data = this.handsontable.getSourceData() as any[];
    const systemTypeMap = this.buildRiserSystemTypeMap(data);
    let hasErrors = false;
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const errors: string[] = [];
      const isFourPipe = row.coil && row.coil.toLowerCase().indexOf('sec') >= 0;
      this.validateDiameterPair(row.coldSupplyTop, row.coldSupplyBottom, 'CldSup', errors);
      this.validateDiameterPair(row.coldReturnTop, row.coldReturnBottom, 'CldRet', errors);
      this.validateDiameterPair(row.drainTop, row.drainBottom, 'Drain', errors);
      if (isFourPipe) {
        this.validateDiameterPair(row.hotSupplyTop, row.hotSupplyBottom, 'HotSup', errors);
        this.validateDiameterPair(row.hotReturnTop, row.hotReturnBottom, 'HotRet', errors);
      } else {
        this.validateTwoPipeHotDiameters(row.hotSupplyTop, row.hotSupplyBottom, 'HotSup', errors);
        this.validateTwoPipeHotDiameters(row.hotReturnTop, row.hotReturnBottom, 'HotRet', errors);
      }
      this.validateAdjacentFloors(data, i, errors, isFourPipe);
      if (row.extensionLength > 0 && row.extensionLocation !== 'Top' && row.extensionLocation !== 'Bottom') {
        errors.push('Ext loc must be Top/Bottom');
      }
      if ((row.extensionLocation === 'Top' || row.extensionLocation === 'Bottom') && (!row.extensionLength || parseFloat(row.extensionLength) === 0)) {
        errors.push('Ext length required when ext location is set');
      }
      if (row.extensionLength > 0 && row.extensionLocation === 'Top') {
        if (
          (row.coldSupplyTop == 0 && row.coldSupplyBottom != 0) ||
          (row.coldReturnTop == 0 && row.coldReturnBottom != 0) ||
          (isFourPipe && row.hotSupplyTop == 0 && row.hotSupplyBottom != 0) ||
          (isFourPipe && row.hotReturnTop == 0 && row.hotReturnBottom != 0)
        ) {
          errors.push('Ext on capped top');
        }
      }
      if (row.extensionLength > 0 && row.extensionLocation === 'Bottom') {
        if (
          (row.coldSupplyBottom == 0 && row.coldSupplyTop != 0) ||
          (row.coldReturnBottom == 0 && row.coldReturnTop != 0) ||
          (isFourPipe && row.hotSupplyBottom == 0 && row.hotSupplyTop != 0) ||
          (isFourPipe && row.hotReturnBottom == 0 && row.hotReturnTop != 0)
        ) {
          errors.push('Ext on capped bot');
        }
      }
      const warnings: string[] = [];
      this.checkInsulationSfr(row.coldSupplyInsulation, row.coldSupplyTop, row.coldSupplyBottom, 'CldSup', warnings);
      this.checkInsulationSfr(row.coldReturnInsulation, row.coldReturnTop, row.coldReturnBottom, 'CldRet', warnings);
      this.checkInsulationSfr(row.drainInsulation, row.drainTop, row.drainBottom, 'Drain', warnings);
      if (isFourPipe) {
        this.checkInsulationSfr(row.hotSupplyInsulation, row.hotSupplyTop, row.hotSupplyBottom, 'HotSup', warnings);
        this.checkInsulationSfr(row.hotReturnInsulation, row.hotReturnTop, row.hotReturnBottom, 'HotRet', warnings);
      } else {
        if (!StringUtils.isZeroOrEmpty(+row.hotSupplyInsulation)) {
          errors.push('HotSup insulation must be "None" for 2-pipe systems');
        }
        if (!StringUtils.isZeroOrEmpty(+row.hotReturnInsulation)) {
          errors.push('HotRet insulation must be "None" for 2-pipe systems');
        }
      }
      this.checkDrainDiameters(row.drainTop, row.drainBottom, warnings);
      const cabinetSpacingWarning: string | undefined = this.getCabinetSpacingWarning(row, systemTypeMap.get(this.getRiserKey(row)) || 'none');
      if (cabinetSpacingWarning) {
        warnings.push(cabinetSpacingWarning);
      }
      const len = parseFloat(row.length) || 0;
      if (!this.isValidHalfInch(len)) {
        errors.push('Length must be in half-inch increments');
      } else if (len < 76 || len > 120) {
        errors.push('Length must be between 76 and 120 inches');
      } else if (len >= 76 && len < 82) {
        warnings.push('Non-standard length (' + len + '"), SFR will be required');
      }
      const extLen = parseFloat(row.extensionLength) || 0;
      if (extLen > 0 && !this.isValidHalfInch(extLen)) {
        errors.push('Ext length must be in half-inch increments');
      } else if (extLen > 0 && (extLen < 6 || extLen > 75)) {
        warnings.push('Non-standard ext length (' + extLen + '"), SFR will be required');
      }
      if (errors.length > 0 && warnings.length > 0) {
        row.status = errors.join('; ') + '; Note: ' + warnings.join('; ');
        hasErrors = true;
      } else if (errors.length > 0) {
        row.status = errors.join('; ');
        hasErrors = true;
      } else if (warnings.length > 0) {
        row.status = 'Note: ' + warnings.join('; ');
      } else {
        row.status = 'OK';
      }
    }
    this.hasValidationErrors.set(hasErrors);
    this.handsontable.render();
  }

  private calculateTableHeight(container: HTMLElement): number {
    const rect = container.getBoundingClientRect();
    const bottomMargin = 90;
    const available = window.innerHeight - rect.top - bottomMargin;
    return Math.max(available, 300);
  }

  private isValidHalfInch(value: number): boolean {
    const remainder = (value * 10) % 5;
    return remainder === 0;
  }

  private checkInsulationSfr(insulation: string, pipeTop: number, pipeBottom: number, label: string, warnings: string[]): void {
    const parsed = parseFloat(insulation);
    const insul = isNaN(parsed) ? 0 : parsed;
    const top = parseFloat(pipeTop as any) || 0;
    const bot = parseFloat(pipeBottom as any) || 0;
    if (top < 0 && bot < 0) return;
    if (insul === 0 && (top > 0 || bot > 0)) {
      warnings.push(label + ' no insulation, SFR will be required');
    }
  }

  private checkDrainDiameters(top: number, bottom: number, warnings: string[]): void {
    const drainTop = StringUtils.isZeroOrEmpty(top) ? 0 : +top;
    const drainBottom = StringUtils.isZeroOrEmpty(bottom) ? 0 : +bottom;
    if (drainTop < 0 && drainBottom < 0) return;
    if (VALID_DIAMETERS.indexOf(drainTop) >= 0 && VALID_DIAMETERS.indexOf(drainBottom) >= 0) {
      if (DRAIN_VALID_DIAMETERS.indexOf(drainTop) < 0) {
        warnings.push('Drain top diameter is non-standard, SFR will be required');
      }
      if (DRAIN_VALID_DIAMETERS.indexOf(drainBottom) < 0) {
        warnings.push('Drain bottom diameter is non-standard, SFR will be required');
      }
    }
  }

  private getRiserKey(row: any): string {
    return (row.building || '') + '|' + (row.riser || '');
  }

  private buildRiserSystemTypeMap(gridRows: any[]): Map<string, RiserSystemType> {
    const gridKeys = new Set<string>(gridRows.map((r) => r.lineId + '|' + r.sequence));
    const hiddenRows = this.riserData().filter((r) => !gridKeys.has(r.lineId + '|' + r.sequence));
    const heating = new Set<string>();
    const cooling = new Set<string>();
    for (const row of [...gridRows, ...hiddenRows]) {
      const key = this.getRiserKey(row);
      if (this.getMaxPipeDiameter(row.coldSupplyTop, row.coldSupplyBottom) > 0 || this.getMaxPipeDiameter(row.coldReturnTop, row.coldReturnBottom) > 0) {
        cooling.add(key);
      }
      if (this.getMaxPipeDiameter(row.hotSupplyTop, row.hotSupplyBottom) > 0 || this.getMaxPipeDiameter(row.hotReturnTop, row.hotReturnBottom) > 0) {
        heating.add(key);
      }
    }
    const systemTypes = new Map<string, RiserSystemType>();
    for (const key of new Set([...heating, ...cooling])) {
      systemTypes.set(key, heating.has(key) && cooling.has(key) ? '4pipe' : heating.has(key) ? 'heating' : 'cooling');
    }
    return systemTypes;
  }

  private getMaxPipeDiameter(top: any, bottom: any): number {
    const t = parseFloat(top) || 0;
    const b = parseFloat(bottom) || 0;
    return Math.max(t, b, 0);
  }

  private getDiameterWithInsulation(top: any, bottom: any, insulation: any): number {
    const diameter = this.getMaxPipeDiameter(top, bottom);
    if (diameter === 0) {
      return 0;
    }
    const rawInsulation = parseFloat(insulation) || 0;
    const thickness = rawInsulation >= 5 ? rawInsulation / 100 : rawInsulation;
    const tubeOutsideDiameter = TUBE_OUTSIDE_DIAMETERS[diameter] || 0;
    return tubeOutsideDiameter + 2 * thickness;
  }

  private getCabinetTierWidth(modelSize: string): number[] {
    const match = /^([A-Z]+)(\d{2})$/.exec((modelSize || '').trim().toUpperCase());
    if (!match) {
      return [];
    }
    const size = match[2];
    if (MEGA_MOD_SIZES.indexOf(size) >= 0) {
      return [MEGA_MOD_WIDTH];
    }
    const tier = CABINET_SIZE_TIERS[size];
    return tier === undefined ? [] : CABINET_TIER_WIDTHS.slice(tier);
  }

  private calculateSideHalfWidth(chain: number[], standardGaps: number[], drainInsulatedDiameter: number): number {
    let previous = drainInsulatedDiameter;
    let distance = 0;
    for (let i = 0; i < chain.length; i++) {
      const required = previous / 2 + chain[i] / 2;
      const standard = standardGaps[i];
      distance += required > standard + 1e-9 ? Math.ceil(required / 0.25 - 1e-9) * 0.25 : standard;
      previous = chain[i];
    }
    return chain.length ? distance + chain[chain.length - 1] / 2 : 0;
  }

  private getCabinetSpacingWarning(row: any, systemType: RiserSystemType): string | undefined {
    const ladder = this.getCabinetTierWidth(row.modelSize);
    if (systemType !== 'none' && ladder.length > 0) {
      const drain = this.getDiameterWithInsulation(row.drainTop, row.drainBottom, row.drainInsulation);
      const coldSupply = this.getDiameterWithInsulation(row.coldSupplyTop, row.coldSupplyBottom, row.coldSupplyInsulation);
      const coldReturn = this.getDiameterWithInsulation(row.coldReturnTop, row.coldReturnBottom, row.coldReturnInsulation);
      const hotSupply = this.getDiameterWithInsulation(row.hotSupplyTop, row.hotSupplyBottom, row.hotSupplyInsulation);
      const hotReturn = this.getDiameterWithInsulation(row.hotReturnTop, row.hotReturnBottom, row.hotReturnInsulation);
      let left = 0;
      let right = 0;
      if (systemType === '4pipe') {
        left = this.calculateSideHalfWidth([hotSupply, hotReturn], [STANDARD_GAP_INNER, STANDARD_GAP_OUTER], drain);
        right = this.calculateSideHalfWidth([coldReturn, coldSupply], [STANDARD_GAP_INNER, STANDARD_GAP_OUTER], drain);
      } else if (systemType === 'heating') {
        left = this.calculateSideHalfWidth([hotReturn], [STANDARD_GAP_INNER], drain);
        right = this.calculateSideHalfWidth([hotSupply], [STANDARD_GAP_INNER], drain);
      } else {
        left = this.calculateSideHalfWidth([coldReturn], [STANDARD_GAP_INNER], drain);
        right = this.calculateSideHalfWidth([coldSupply], [STANDARD_GAP_INNER], drain);
      }
      const requiredWidth = 2 * Math.max(left, right);
      const footprint = (row.cabinetFootprint || 'Std').trim().toUpperCase();
      const footprintStep = CABINET_FOOTPRINT_STEPS[footprint] || 0;
      const currentStep = Math.min(footprintStep, ladder.length - 1);
      if (requiredWidth > ladder[currentStep] + 1e-9) {
        const fitStep = ladder.findIndex((width) => width + 1e-9 >= requiredWidth);
        return fitStep < 0 ? SPACING_TOO_SMALL_NOTE : UPSIZE_NOTES[fitStep];
      }
    }
    return undefined;
  }

  private validateDiameterPair(top: number, bottom: number, label: string, errors: string[]): void {
    const t = StringUtils.isZeroOrEmpty(+top) ? 0 : +top;
    const b = StringUtils.isZeroOrEmpty(+bottom) ? 0 : +bottom;
    if (t < 0 && b < 0) return;
    if (t === 0 && b === 0) {
      errors.push(label + ' cannot be capped on both ends');
      return;
    }
    if (t === b) return;
    if (t < 0 || b < 0) {
      errors.push(label + ' both top/bottom must be "None" for no riser');
      return;
    }
    if (t === 0 || b === 0) return;
    const tIdx = VALID_DIAMETERS.indexOf(t);
    const bIdx = VALID_DIAMETERS.indexOf(b);
    if (tIdx < 0 || bIdx < 0 || Math.abs(tIdx - bIdx) > 1) {
      errors.push(label + ' dia invalid');
    }
  }

  private validateTwoPipeHotDiameters(top: number, bottom: number, label: string, errors: string[]): void {
    const t = parseFloat(top as any) || 0;
    const b = parseFloat(bottom as any) || 0;
    if (t <= 0 && b <= 0) return;
    errors.push(label + ' must be "Capped" or "None" for 2-pipe systems');
  }

  private validateAdjacentFloors(data: any[], rowIndex: number, errors: string[], isFourPipe: boolean): void {
    const row = data[rowIndex];
    if (!row.riser || !row.floor || !row.building) return;
    const floor = parseInt(row.floor);
    if (isNaN(floor)) return;
    const pipeFields = [
      { top: 'coldSupplyTop', bot: 'coldSupplyBottom', label: 'CldSup' },
      { top: 'coldReturnTop', bot: 'coldReturnBottom', label: 'CldRet' },
      { top: 'drainTop', bot: 'drainBottom', label: 'Drain' },
      ...(isFourPipe
        ? [
            { top: 'hotSupplyTop', bot: 'hotSupplyBottom', label: 'HotSup' },
            { top: 'hotReturnTop', bot: 'hotReturnBottom', label: 'HotRet' },
          ]
        : []),
    ];
    for (const other of data) {
      if (other === row || other.riser !== row.riser || other.building !== row.building) continue;
      const otherFloor = parseInt(other.floor);
      if (isNaN(otherFloor)) continue;

      if (otherFloor === floor + 1) {
        for (const pipe of pipeFields) {
          const thisTop = parseFloat(row[pipe.top]) || 0;
          const otherBot = parseFloat(other[pipe.bot]) || 0;
          if (thisTop < 0 || otherBot < 0) continue;
          if (thisTop !== 0 && otherBot !== thisTop) {
            errors.push(pipe.label + ' floor above mismatch');
          }
        }
      }
      if (otherFloor === floor - 1) {
        for (const pipe of pipeFields) {
          const thisBot = parseFloat(row[pipe.bot]) || 0;
          const otherTop = parseFloat(other[pipe.top]) || 0;
          if (thisBot < 0 || otherTop < 0) continue;
          if (thisBot !== 0 && otherTop !== thisBot) {
            errors.push(pipe.label + ' floor below mismatch');
          }
        }
      }
    }
  }

  save(): void {
    if (!this.handsontable) return;
    this.syncEditsToSource();
    this.isBusy.set(true);
    this.messages.set([]);
    const units: RiserChartUnit[] = this.riserData().map((row) => {
      const unit: RiserChartUnit = { ...row };
      delete (unit as any).status;
      return unit;
    });
    this.riserUploadService.saveRiserData(this.entityKey(), units).subscribe({
      next: (result) => {
        this.uploadResponse.set(result);
        this.messages.set(result.messages);
        if (result.messageLevel === MessageLevel.INFO) {
          this.messages.update(current => [...current, 'Success!']);
          this.goBack();
        }
        this.isBusy.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.isBusy.set(false);
        this.messages.update(current => [...current, StringUtils.getErrorMessage(error)]);
      },
    });
  }

  downloadTemplate(): void {
    this.isBusy.set(true);
    this.riserUploadService.getTemplate(this.entityKey()).subscribe({
      next: (result) => {
        FileUtils.downloadFile(result, this.entityKey().entityId + '-Riser_Chart.xlsx');
        this.isBusy.set(false);
      },
      error: (error) => {
        this.isBusy.set(false);
        this.messages.update(current => [...current, 'Error downloading template: ' + StringUtils.getErrorMessage(error)]);
      },
    });
  }

  fileChange(upload: any): void {
    this.isBusy.set(true);
    this.messages.set([]);
    if (upload.target.files && upload.target.files.length > 0) {
      const file = upload.target.files[0];
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const uploadFile = new UploadFile();
        uploadFile.documentName = file.name;
        uploadFile.entityKey = this.entityKey();
        uploadFile.fileBytes = reader.result as unknown as Uint8Array;
        uploadFile.fileName = file.name;
        uploadFile.mimeType = file.type || 'application/octet-stream';
        uploadFile.documentType = 'RISER_CHART';
        this.riserUploadService.uploadRisers(this.entityKey(), uploadFile.fileBytes).subscribe({
          next: (result) => {
            this.messages.set(result.messages);
            this.uploadResponse.set(result);
            if (result.messageLevel === MessageLevel.INFO) {
              this.messages.update(current => [...current, 'Success!']);
              this.goBack();
            } else {
              this.isBusy.set(false);
            }
          },
          error: (error) => {
            this.isBusy.set(false);
            this.messages.update(current => [...current, StringUtils.getErrorMessage(error)]);
          },
        });
      };
    }
  }

  getMessageClass(): string {
    if (this.uploadResponse()) {
      switch (this.uploadResponse().messageLevel) {
        case MessageLevel.ERROR:
          return 'error';
        case MessageLevel.INFO:
          return 'info';
        case MessageLevel.WARNING:
          return 'warn';
      }
    }
    return this.messages().some((m) => m.toLowerCase().startsWith('error')) ? 'error' : 'info';
  }
}
